import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, MessageCircle, ShoppingBag, MapPin } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function CartPage() {
  const { t } = useLang();
  const { groups, count, remove, clear, sendInquiry } = useCart();

  const itemsLabel = (n) =>
    (n === 1 ? t("cp.item_singular") : t("cp.items")).replace("{n}", String(n));

  const handleSend = async (vid) => {
    try {
      const r = await sendInquiry(vid);
      toast.success(t("cp.sent"));
      // Navigate to chat
      window.location.href = `/chat/${r.chat_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("cp.send_failed"));
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">{t("cp.eyebrow")}</div>
          <h1 className="font-heading text-3xl tracking-tighter">{itemsLabel(count)}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("cp.subtitle")}</p>
        </div>
        {count > 0 && (
          <Button variant="outline" onClick={() => clear()} data-testid="cart-clear-btn" className="rounded-full">
            {t("cp.clear_all")}
          </Button>
        )}
      </div>

      {count === 0 ? (
        <Card className="p-10 border-border/60 text-center" data-testid="cart-empty">
          <ShoppingBag className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-3 text-muted-foreground">{t("cp.empty")}</p>
          <Button asChild className="mt-4 rounded-full"><Link to="/vendors">{t("cp.browse_vendors")}</Link></Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <Card key={g.vendor?.vendor_id} className="p-5 border-border/60" data-testid={`cart-group-${g.vendor?.vendor_id}`}>
              <div className="flex items-start gap-3">
                {g.vendor?.logo && <img src={g.vendor.logo} alt="" className="h-12 w-12 rounded-xl object-cover" />}
                <div className="flex-1 min-w-0">
                  <Link to={`/vendors/${g.vendor?.vendor_id}`} className="font-heading text-lg leading-tight hover:text-primary">
                    {g.vendor?.business_name}
                  </Link>
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {g.vendor?.city}</div>
                </div>
                <Badge variant="secondary">{itemsLabel(g.items.length)}</Badge>
              </div>
              <ul className="mt-4 divide-y divide-border/60">
                {g.items.map((it) => (
                  <li key={it.item_id} className="py-2 flex items-center gap-3 text-sm">
                    <span className="flex-1">
                      <strong>{it.product_name}</strong>
                      <span className="text-muted-foreground"> × {it.quantity}</span>
                      {it.note && <span className="text-muted-foreground"> — {it.note}</span>}
                    </span>
                    <button onClick={() => remove(it.item_id)} data-testid={`cart-remove-${it.item_id}`} className="text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex justify-end gap-2">
                <Button onClick={() => handleSend(g.vendor.vendor_id)} className="rounded-full" data-testid={`cart-send-${g.vendor?.vendor_id}`}>
                  <MessageCircle className="h-4 w-4 mr-1" /> {t("cp.send_inquiry")}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
